//UploadClassRecordingModal.jsx

import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import dateFormat from 'dateformat';
import { useParams } from 'react-router';
import { useContext } from 'react';
import { Toast } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { Chip } from '@material-ui/core';
import axios from 'axios';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import CustomModal from './CustomModal';
import { AuthContext } from '../../context/AuthContext';
import ENDPOINT from '../../context/EndPoint';
import { Close } from '@material-ui/icons';
import { connect, useDispatch } from 'react-redux';

const UploadClassRecordingModal = ({ item, visibility, handleClose, value, dispatch, Reload }) => {
  const params = useParams();
  const id = item.id;
  const { user } = useContext(AuthContext);
  const isSubmitting = useRef(false);

  const [toasttitle, settoasttitle] = useState(null);
  const [toastmessage, settoastmessage] = useState(null);
  const [file, setfile] = useState(null);
  const [status, setstatus] = useState(false);
  const [uploadprogress, setuploadprogress] = useState(0);
  const UPLOAD_ENDPOINT = `https://siliconmindstechnologies.com/upload-recording.php`;
  const [toast, settoast] = useState(false);
  const [tags, settags] = useState([]);
  const [videolength, setvideolength] = useState(null);
  const [currenttag, setcurrenttag] = useState(null);
  const [display, setdisplay] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Build the video preview URL ONCE per selected file (not on every render).
  // Otherwise the upload-progress re-renders kept generating a new blob URL, so
  // the <video> src changed on every tick and the modal "blinked" while uploading.
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const getDuration = () => {
    const video = document.getElementById("video");
    if (video) {
      setvideolength(video.duration);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting.current || !file || !videolength) {
      if (!file) {
        settoastmessage('Please select a video file.');
        settoasttitle('No File Selected');
        settoast(true);
      } else if (!videolength) {
        settoastmessage('Video duration could not be determined.');
        settoasttitle('Invalid Video');
        settoast(true);
      }
      return;
    }
    isSubmitting.current = true;
    setstatus(true);
    setuploadprogress(0);
    dispatch({ type: 'LOADING' });

    const finishSuccess = () => {
      dispatch({ type: 'DONE_LOADING' });
      setuploadprogress(100);
      settoastmessage('Recording uploaded.');
      settoasttitle('Recording uploaded.');
      settoast(true);
      sessionStorage.setItem('iid', null);
      handleClose();
      Reload();
    };

    try {
      const res = await uploadFileChunked(file);
      const data = res && res.data ? res.data : {};
      if (data.status === 'success' && data.message === 'applied') {
        notifyStudents(); // fire-and-forget; must never block the upload UX
        finishSuccess();
      } else {
        // response wasn't a clean success — the file may still have saved
        const saved = await verifyRecordingSaved();
        if (saved) {
          finishSuccess();
        } else {
          const msg = data.message ? data.message : 'Please try again.';
          dispatch({ type: 'ERROR', payload: msg });
          setuploadprogress(0);
          settoastmessage(msg);
          settoasttitle('Upload failed');
          settoast(true);
        }
      }
    } catch (error) {
      // network / unexpected — check whether it actually saved before failing
      const saved = await verifyRecordingSaved();
      if (saved) {
        finishSuccess();
      } else {
        dispatch({ type: 'ERROR', payload: error.message });
        setuploadprogress(0);
        settoastmessage('An error occurred. Please try again.');
        settoasttitle('Error');
        settoast(true);
      }
    } finally {
      isSubmitting.current = false;
      setstatus(false);
    }
  };

  const onChange = (e) => {
    setfile(e.target.files[0]);
  };

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB parts survive flaky networks
  const MAX_CHUNK_RETRIES = 4;

  const getSession = () => {
    const stored = JSON.parse(localStorage.getItem('user'));
    return stored ? stored.SessionId : user ? user.SessionId : null;
  };

  // Resumable chunked upload: send the video in small parts (each retried on
  // failure), then finalize so the server forwards it to Bunny in one go.
  const uploadFileChunked = async (file) => {
    const session = getSession();
    const uploadId = `${item.id}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    const filename = file.name || 'recording.mp4';

    for (let index = 0; index < totalChunks; index++) {
      const start = index * CHUNK_SIZE;
      const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));

      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const fd = new FormData();
          fd.append('session', session);
          fd.append('action', 'chunk');
          fd.append('upload_id', uploadId);
          fd.append('chunk_index', index);
          fd.append('total_chunks', totalChunks);
          fd.append('filename', filename);
          fd.append('chunk', blob);

          const res = await axios.post(UPLOAD_ENDPOINT, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
            onUploadProgress: (e) => {
              const frac = e.total ? e.loaded / e.total : 1;
              const overall = ((index + frac) / totalChunks) * 100;
              setuploadprogress(Math.min(99, Math.round(overall)));
            },
          });
          if (res.data && res.data.status === 'success') break;
          throw new Error(
            (res.data && res.data.message) || `Chunk ${index} failed`,
          );
        } catch (err) {
          attempt++;
          if (attempt > MAX_CHUNK_RETRIES) throw err;
          await new Promise((r) => setTimeout(r, 1200 * attempt)); // backoff
        }
      }
    }

    setuploadprogress(99);

    // finalize — server streams the stored parts to Bunny and saves the URL
    const fd = new FormData();
    fd.append('session', session);
    fd.append('action', 'finalize');
    fd.append('upload_id', uploadId);
    fd.append('filename', filename);
    fd.append('total_chunks', totalChunks);
    fd.append('class_id', item.id);
    fd.append('course_url', item.course_url);
    fd.append('batch_id', item.batch_id);
    fd.append('duration', videolength);
    fd.append('class_starts', item.class_starts);
    if (value.notification) fd.append('notification', value.notification);

    return await axios.post(UPLOAD_ENDPOINT, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // forwarding a big file to the CDN can take a while
    });
  };

  // Notifications run as a SEPARATE request so they can never slow down or
  // fail the upload itself. Fire-and-forget: we don't await the result.
  const notifyStudents = () => {
    try {
      const fd = new FormData();
      fd.append('session', getSession());
      fd.append('action', 'notify');
      fd.append('class_id', item.id);
      if (value.notification) fd.append('notification', value.notification);
      axios
        .post(UPLOAD_ENDPOINT, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 15000,
        })
        .catch(() => {});
    } catch (e) {
      /* ignore — notifications are best-effort */
    }
  };

  // Safety net: after any error, ask the server whether the recording is
  // actually saved (uploads often succeed even when the response hiccups).
  const verifyRecordingSaved = async () => {
    try {
      const fd = new FormData();
      fd.append('session', getSession());
      fd.append('action', 'verify');
      fd.append('class_id', item.id);
      const res = await axios.post(UPLOAD_ENDPOINT, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 20000,
      });
      return !!(
        res.data &&
        res.data.status === 'success' &&
        res.data.recording
      );
    } catch (e) {
      return false;
    }
  };

  const updateTags = () => {
    if (currenttag) {
      settags([...tags, currenttag]);
      setcurrenttag('');
      setdisplay(true);
    }
  };

  const RemoveTag = ({ index }) => {
    setdisplay(false);
    const updatedTags = [...tags];
    updatedTags.splice(index, 1);
    settags(updatedTags);
    setdisplay(true);
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <CustomModal visibility={visibility} handleClose={handleClose}>
        <div className="position-fixed container-fluid justify-center bottom-60 left-0">
          <Toast className="bg-warning" onClose={() => settoast(false)} show={toast}>
            <Toast.Header>
              <img src="holder.js/20x20?text=%20" className="rounded me-2" alt="" />
              <strong className="me-auto">{toasttitle}</strong>
            </Toast.Header>
            <Toast.Body>{toastmessage}</Toast.Body>
          </Toast>
        </div>

        <div className="pb-0">
          <div className="modal-header">
            <h4>Upload Recording</h4>
            <Close onClick={handleClose} />
          </div>
          <hr />
          <div className="">
            <div className="">
              <div className="w-100">
                {previewUrl ? (
                  <video
                    controls
                    className="w-100 mx-auto"
                    id="video"
                    onLoadedData={getDuration}
                    src={previewUrl}
                  ></video>
                ) : (
                  <video className="w-100 mx-auto bg-dark"></video>
                )}
                <div className="w-100">
                  <div className="d-flex justify-content-between">
                    <p>Class Time: {dateFormat(item.class_starts, 'dS mmm hh:mm tt')}</p>
                    <p>Duration: {videolength ? Math.round(videolength / 60) : 0} min</p>
                  </div>
                </div>
              </div>
              <div className="w-100">
                <p>Course: {item.course_url}</p>
                <div className="d-flex justify-content-between">
                  <p>Batch ID: {item.batch_id}</p>
                  <p>Class ID: {item.id}</p>
                </div>
                <hr />
                <form onSubmit={onSubmit} className="position-relative">
                  {file ? (
                    <div className="container-fluid">
                      {tags && display ? (
                        tags.map((tag, index) => (
                          <Chip
                            key={tag}
                            className="mr-1 btn btn-danger"
                            onClick={() => RemoveTag({ index })}
                            label={tag}
                          />
                        ))
                      ) : null}
                    </div>
                  ) : null}
                  <div className="justify-center">
                    <label htmlFor="file-input">Upload Recording</label>
                    <input
                      id="file-input"
                      type="file"
                      className=""
                      accept="video/*"
                      placeholder="Video"
                      onChange={onChange}
                    />
                  </div>
                  {file ? (
                    <>
                      <br />
                      <button
                        className="btn btn-primary w-100 mt-2"
                        type="submit"
                        disabled={status || uploadprogress > 0}
                      >
                        {status || uploadprogress > 0 ? (
                          <>
                            <CircularProgress className="text-white" size={20} />{' '}
                            {uploadprogress > 0 ? `Uploading ${uploadprogress}%...` : 'Uploading...'}
                          </>
                        ) : (
                          'Upload Video'
                        )}
                      </button>
                    </>
                  ) : null}
                </form>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

const Notification = (prop) => ({
  value: prop.sendNotification,
});

export default connect(Notification)(UploadClassRecordingModal);